/**
 * 大会本部のパイバル配信（docs/08 §198）をブリッジから受け取る。
 *
 * ブリッジがオクレンジャーから取り込んで貯めたもの（`GET /api/pibal`）を、
 * アプリが開いている間ときどき見に行く（ETagで、変わっていなければ中身を
 * 受け取らない）。最後に受け取ったものは端末に残し、圏外でも見られるようにする。
 * 本文を貼り付けて足したもの（ブリッジに届かないときの予備）は端末にだけ残す。
 */

import {
  parseOfficialPibal,
  parseTaskMarkerNotice,
  sortPibalsNewestFirst,
  stripChatHeaders,
  type OfficialPibal,
  type TaskMarkerNotice,
} from '@gv4d/core';

export interface PibalWindow {
  start: string;
  end: string;
}

/** ブリッジの `/api/pibal` の応答（apps/bridge/src/pibal-service.ts の PibalPublicState） */
export interface PibalFeedState {
  config: {
    configured: boolean;
    linkHint: string;
    enabled: boolean;
    windows: PibalWindow[];
    intervalMin: number;
    markerWindows?: PibalWindow[];
    markerIntervalMin?: number;
    timeZone: string;
    competitorListUrl?: string | null;
    /** 今の大会の名前（風のアーカイブの名前、docs/08 §212） */
    archiveName?: string | null;
  };
  status: {
    lastAttemptAt: number | null;
    lastSuccessAt: number | null;
    lastError: string | null;
    running: boolean;
    inWindow: boolean;
    /** WhatsApp等の受信プログラムの状態（docs/08 §203）。古いブリッジ・受信プログラムなしでは無い */
    receiver?: {
      source: string;
      connected: boolean;
      lastMessageAt: number | null;
      error: string | null;
      pending: number;
      groups: number;
      reportedAt: number;
    } | null;
  };
  soundings: OfficialPibal[];
  /** 計測するマーカーのお知らせ（docs/08 §200）。古いブリッジには無い */
  markerNotices?: TaskMarkerNotice[];
  /** 競技者一覧（番号と名前） */
  competitors?: Array<{ no: number; name: string; from: string }>;
  /** 過去の大会の風のアーカイブの一覧（docs/08 §212）。古いブリッジには無い */
  archives?: PibalArchiveSummary[];
  version: number;
}

export interface PibalArchiveSummary {
  id: string;
  name: string;
  updatedAt: number;
  count: number;
  firstLocal: string | null;
  lastLocal: string | null;
}

/** 端末で貼り付けて足したもの（`local: true`） */
export type FeedPibal = OfficialPibal & { local?: boolean };
export type FeedMarkerNotice = TaskMarkerNotice & { local?: boolean };

const CACHE_KEY = 'gv4d-official-pibal-cache';
const PASTED_KEY = 'gv4d-official-pibal-pasted';
const PASTED_MARKERS_KEY = 'gv4d-official-markers-pasted';
const SEEN_KEY = 'gv4d-official-pibal-seen';

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 保存できなくても今回は使える
  }
}

/** ブリッジのものと貼り付けたものを合わせ、新しい順に（同じ配信はブリッジ側を優先） */
export function mergePibals(server: OfficialPibal[], pasted: OfficialPibal[]): FeedPibal[] {
  const ids = new Set(server.map((s) => s.id));
  const sameContent = (a: OfficialPibal, b: OfficialPibal) =>
    a.sentLocal.slice(0, 10) === b.sentLocal.slice(0, 10) && JSON.stringify(a.levels) === JSON.stringify(b.levels);
  const extra = pasted.filter((p) => !ids.has(p.id) && !server.some((s) => sameContent(s, p)));
  return sortPibalsNewestFirst([...server, ...extra.map((p) => ({ ...p, local: true }))]);
}

/** ブリッジのものと貼り付けたものを合わせ、新しい順に（同じお知らせはブリッジ側を優先） */
export function mergeMarkerNotices(server: TaskMarkerNotice[], pasted: TaskMarkerNotice[]): FeedMarkerNotice[] {
  const ids = new Set(server.map((s) => s.id));
  const same = (a: TaskMarkerNotice, b: TaskMarkerNotice) =>
    a.taskNo === b.taskNo && a.target === b.target && a.sentLocal.slice(0, 10) === b.sentLocal.slice(0, 10) && a.markers.join() === b.markers.join();
  const extra = pasted.filter((p) => !ids.has(p.id) && !server.some((s) => same(s, p))).map((p) => ({ ...p, local: true }));
  return [...server, ...extra].sort((a, b) => b.sentLocal.localeCompare(a.sentLocal) || b.taskNo - a.taskNo);
}

/** 新しい順の一覧から、まとまり（午前・午後）を新しい順に */
export function sessionsOf(list: OfficialPibal[]): string[] {
  const out: string[] = [];
  for (const p of list) if (!out.includes(p.session)) out.push(p.session);
  return out.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 端末の現地時刻 YYYY-MM-DDTHH:MM（貼り付けたときの配信時刻の代わり） */
export function localNowString(d = new Date()): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export class OfficialPibalFeed {
  #baseUrl: () => string;
  #state: PibalFeedState | null = readJson<PibalFeedState>(CACHE_KEY);
  #pasted: OfficialPibal[] = readJson<OfficialPibal[]>(PASTED_KEY) ?? [];
  #pastedMarkers: TaskMarkerNotice[] = readJson<TaskMarkerNotice[]>(PASTED_MARKERS_KEY) ?? [];
  #archiveCache = new Map<string, { updatedAt: number; soundings: OfficialPibal[] }>();
  #etag: string | null = null;
  #timer: ReturnType<typeof setTimeout> | null = null;
  #listeners = new Set<() => void>();
  /** 最後の取得の結果（画面の案内用） */
  lastFetchError: string | null = null;
  needsAuth = false;

  constructor(baseUrl: () => string) {
    this.#baseUrl = () => (baseUrl() || location.origin).replace(/\/+$/, '');
  }

  get state(): PibalFeedState | null {
    return this.#state;
  }

  get pibals(): FeedPibal[] {
    return mergePibals(this.#state?.soundings ?? [], this.#pasted);
  }

  get markerNotices(): FeedMarkerNotice[] {
    return mergeMarkerNotices(this.#state?.markerNotices ?? [], this.#pastedMarkers);
  }

  get competitors(): Array<{ no: number; name: string; from: string }> {
    return this.#state?.competitors ?? [];
  }

  onChange(listener: () => void): void {
    this.#listeners.add(listener);
  }

  #emit(): void {
    for (const l of this.#listeners) l();
  }

  /** いちばん新しいパイバルと計測のお知らせの組（新着かどうかの判定に使う） */
  #latestKey(): string {
    return `${this.pibals[0]?.id ?? ''}|${this.markerNotices[0]?.id ?? ''}`;
  }

  /** 新着（まだ開いて見ていない最新の配信）があるか */
  hasUnseen(): boolean {
    const key = this.#latestKey();
    return key !== '|' && readJson<string>(SEEN_KEY) !== key;
  }

  markSeen(): void {
    const key = this.#latestKey();
    if (key !== '|') writeJson(SEEN_KEY, key);
  }

  /** ブリッジに貯まっているものを見に行く。変わっていれば知らせる */
  async refresh(): Promise<void> {
    try {
      const res = await fetch(`${this.#baseUrl()}/api/pibal`, {
        credentials: 'same-origin',
        headers: this.#etag ? { 'If-None-Match': this.#etag } : {},
        cache: 'no-store',
      });
      this.needsAuth = res.status === 401;
      if (res.status === 304) {
        this.lastFetchError = null;
        return;
      }
      if (!res.ok) {
        this.lastFetchError = res.status === 401 ? '合言葉が必要です（接続から入ってください）' : `HTTP ${res.status}`;
        this.#emit();
        return;
      }
      this.#etag = res.headers.get('etag');
      this.#state = (await res.json()) as PibalFeedState;
      this.lastFetchError = null;
      writeJson(CACHE_KEY, this.#state);
      this.#emit();
    } catch (e) {
      this.lastFetchError = `ブリッジにつながりません（${(e as Error).message}）`;
      this.#emit();
    }
  }

  /** `intervalMs`おきに見に行く（画面が隠れている間は行かない） */
  startPolling(intervalMs: number): void {
    this.stopPolling();
    const loop = async () => {
      if (document.visibilityState === 'visible') await this.refresh();
      this.#timer = setTimeout(loop, intervalMs);
    };
    void loop();
  }

  stopPolling(): void {
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
  }

  /** ブリッジに「今すぐオクレンジャーを読みに行く」よう頼む */
  async fetchNow(): Promise<void> {
    const res = await fetch(`${this.#baseUrl()}/api/pibal/fetch`, { method: 'POST', credentials: 'same-origin' });
    if (!res.ok) throw new Error(res.status === 401 ? '合言葉が必要です' : `HTTP ${res.status}`);
    this.#state = (await res.json()) as PibalFeedState;
    this.#etag = null;
    writeJson(CACHE_KEY, this.#state);
    this.#emit();
  }

  /**
   * 本文をブリッジへ送り、GV4Dを使っている全員に配る（POST /api/pibal/submit、docs/08 §202）。
   * 結果の一行（「パイバル（放球 13:30、15高度）を取り込みました」など）を返す。
   * 読めなかった・送れなかったときは Error
   */
  async submit(text: string, source = 'app'): Promise<string> {
    const res = await fetch(`${this.#baseUrl()}/api/pibal/submit`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, source }),
    });
    const body = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
    if (res.status === 401) throw new Error('合言葉が必要です（接続から入ってください）');
    if (!res.ok || !body.ok) throw new Error(body.message ?? `HTTP ${res.status}`);
    this.#etag = null;
    await this.refresh();
    return body.message ?? '送りました';
  }

  /** 取り込みの設定（管理者用パスワードが要る）。パスワードは送るだけで保存しない */
  async saveConfig(password: string, patch: Record<string, unknown>): Promise<void> {
    const res = await fetch(`${this.#baseUrl()}/api/pibal/config`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...patch, password }),
    });
    const body = (await res.json().catch(() => ({}))) as Partial<PibalFeedState> & { error?: string };
    if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
    this.#state = body as PibalFeedState;
    this.#etag = null;
    writeJson(CACHE_KEY, this.#state);
    this.#emit();
  }

  /** 過去の大会の風のアーカイブの一覧（新しい大会から） */
  get archives(): PibalArchiveSummary[] {
    return this.#state?.archives ?? [];
  }

  /** アーカイブの中身（新しい順）。一度読んだものは覚えておく（中身は足されることがあるので更新日時で見分ける） */
  async loadArchive(id: string): Promise<OfficialPibal[]> {
    const summary = this.archives.find((a) => a.id === id);
    const cached = this.#archiveCache.get(id);
    if (cached && summary && cached.updatedAt === summary.updatedAt) return cached.soundings;
    const res = await fetch(`${this.#baseUrl()}/api/pibal/archive/${encodeURIComponent(id)}`, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error(res.status === 404 ? 'そのアーカイブはありません' : `HTTP ${res.status}`);
    const body = (await res.json()) as { updatedAt: number; soundings: OfficialPibal[] };
    this.#archiveCache.set(id, { updatedAt: body.updatedAt, soundings: body.soundings });
    return body.soundings;
  }

  /** アーカイブを CSV で取り出す URL（同じオリジンなので合言葉の Cookie が乗る） */
  archiveCsvUrl(id: string): string {
    return `${this.#baseUrl()}/api/pibal/archive/${encodeURIComponent(id)}?format=csv`;
  }

  /** この端末にだけ足した（貼り付けた）ものを消す。取り込んだ情報を消すときに一緒に（docs/08 §211） */
  clearLocal(): void {
    this.#pasted = [];
    this.#pastedMarkers = [];
    writeJson(PASTED_KEY, this.#pasted);
    writeJson(PASTED_MARKERS_KEY, this.#pastedMarkers);
    this.#emit();
  }

  /**
   * 本文を貼り付けて足す（端末にだけ残す）。パイバルか計測のお知らせとして
   * 読めたものを返す。読めなければ null
   */
  addPasted(
    text: string,
    now = new Date(),
  ): { kind: 'pibal'; pibal: OfficialPibal } | { kind: 'marker'; notice: TaskMarkerNotice } | null {
    // WhatsAppでまとめてコピーした頭書き（[14:49, 2025/11/3] 名前:）を外し、その時刻を配信時刻の手がかりに
    const stripped = stripChatHeaders(text);
    text = stripped.text;
    const options = {
      reference: { year: now.getFullYear(), month: now.getMonth() + 1 },
      fallbackSentLocal: stripped.sentLocal ?? localNowString(now),
    };
    const notice = parseTaskMarkerNotice(text, options);
    if (notice) {
      this.#pastedMarkers = [...this.#pastedMarkers.filter((p) => p.id !== notice.id), notice].slice(-60);
      writeJson(PASTED_MARKERS_KEY, this.#pastedMarkers);
      this.#emit();
      return { kind: 'marker', notice };
    }
    const parsed = parseOfficialPibal(text, options);
    if (!parsed) return null;
    this.#pasted = [...this.#pasted.filter((p) => p.id !== parsed.id), parsed].slice(-60);
    writeJson(PASTED_KEY, this.#pasted);
    this.#emit();
    return { kind: 'pibal', pibal: parsed };
  }

  removePasted(id: string): void {
    this.#pasted = this.#pasted.filter((p) => p.id !== id);
    writeJson(PASTED_KEY, this.#pasted);
    this.#emit();
  }
}
